import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, LogOut } from "lucide-react";
import { BUSINESS } from "../data";
import { useAuth, startGoogleLogin } from "../lib/auth";

const SECTION_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

const PAGE_LINKS = [
  { label: "Memberships", to: "/memberships" },
  { label: "About", to: "/about" },
];

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goPage = (to) => {
    setOpen(false);
    navigate(to);
    window.scrollTo({ top: 0 });
  };

  const alwaysDark = location.pathname !== "/";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || alwaysDark
          ? "backdrop-blur-2xl bg-black/60 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => goPage("/")}
          className="flex flex-col items-start leading-none"
        >
          <span className="font-heading text-xl md:text-2xl font-extrabold tracking-tighter text-white">
            MAREZ
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Detailing
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {SECTION_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => goSection(l.href)}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          {PAGE_LINKS.map((l) => (
            <button
              key={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => goPage(l.to)}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <Phone size={15} />
            {BUSINESS.phone}
          </a>
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/15" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white text-xs font-semibold">
                  {(user.name || user.email || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="text-zinc-400 hover:text-white p-2 rounded-full hover:bg-white/5 transition-colors"
                aria-label="Sign out"
              >
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-signin-btn"
              onClick={() => startGoogleLogin("/")}
              className="hidden sm:inline-flex items-center rounded-full border border-white/20 text-white text-sm px-4 py-2 hover:bg-white/5 transition-colors"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="nav-book-btn"
            onClick={onBook}
            className="btn-3d gloss-sweep hidden sm:inline-flex items-center rounded-full bg-[#fafafa] text-[#050505] text-sm font-semibold px-5 py-2.5 hover:bg-white"
          >
            Book Now
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden backdrop-blur-2xl bg-black/90 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {SECTION_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => goSection(l.href)}
                  className="text-left text-lg text-zinc-300 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              {PAGE_LINKS.map((l) => (
                <button
                  key={l.to}
                  onClick={() => goPage(l.to)}
                  className="text-left text-lg text-zinc-300 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-btn"
                onClick={() => {
                  setOpen(false);
                  onBook();
                }}
                className="mt-2 rounded-full bg-[#fafafa] text-[#050505] font-semibold px-5 py-3"
              >
                Book Your Detail
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
